/**
 * WeaveIn.ai Platform - Break Glass Emergency Access API Service
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface BreakGlassEvent {
  id: number;
  cbo_id: number;
  initiated_by: number;
  initiated_by_name: string | null;
  member_id: number | null;
  program_id: number | null;
  reason: string;
  justification_category: string | null;
  fields_accessed: string[] | null;
  access_duration_minutes: number;
  expires_at: string;
  is_active: boolean;
  review_status: string;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  review_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface BreakGlassInitiateRequest {
  member_id?: number | null;
  reason: string;
  justification_category: string;
  access_duration_minutes?: number;
}

export interface BreakGlassReviewRequest {
  review_status: string;
  review_notes: string;
}

export interface BreakGlassListResponse {
  events: BreakGlassEvent[];
  total: number;
  page: number;
  page_size: number;
}

export interface BreakGlassListParams {
  status?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface BreakGlassDeleteProgramDataRequest {
  member_id: number;
  program_id: number;
  reason: string;
  confirm_deletion: boolean;
}

export interface BreakGlassDeleteProgramDataResponse {
  success: boolean;
  break_glass_event_id: number;
  member_id: number;
  program_id: number;
  deleted_records: Record<string, number>;
  message: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class BreakGlassAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async initiateBreakGlass(
    cboId: number,
    data: BreakGlassInitiateRequest
  ): Promise<BreakGlassEvent> {
    const response = await this.client.post<BreakGlassEvent>(
      `/api/organizations/${cboId}/break-glass/initiate`,
      data
    );
    return response.data;
  }

  async listEvents(
    cboId: number,
    params?: BreakGlassListParams
  ): Promise<BreakGlassListResponse> {
    const response = await this.client.get<BreakGlassListResponse>(
      `/api/organizations/${cboId}/break-glass/events`,
      { params }
    );
    return response.data;
  }

  async getActiveEvents(cboId: number): Promise<BreakGlassEvent[]> {
    const response = await this.client.get<BreakGlassEvent[]>(
      `/api/organizations/${cboId}/break-glass/active`
    );
    return response.data;
  }

  async getEvent(cboId: number, eventId: number): Promise<BreakGlassEvent> {
    const response = await this.client.get<BreakGlassEvent>(
      `/api/organizations/${cboId}/break-glass/${eventId}`
    );
    return response.data;
  }

  async reviewEvent(
    cboId: number,
    eventId: number,
    data: BreakGlassReviewRequest
  ): Promise<BreakGlassEvent> {
    const response = await this.client.post<BreakGlassEvent>(
      `/api/organizations/${cboId}/break-glass/${eventId}/review`,
      data
    );
    return response.data;
  }

  async deleteProgramData(
    cboId: number,
    data: BreakGlassDeleteProgramDataRequest
  ): Promise<BreakGlassDeleteProgramDataResponse> {
    const response = await this.client.post<BreakGlassDeleteProgramDataResponse>(
      `/api/organizations/${cboId}/break-glass/delete-program-data`,
      data
    );
    return response.data;
  }
}

export const breakGlassAPI = new BreakGlassAPI();
